import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Camera, ChevronDown, ChevronUp, RotateCcw, Save } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { ShotDrop } from "./ShotDrop";

const f1 = (n) => (Number.isFinite(n) ? n.toFixed(1) : "—");
const sg = (n) => (Number.isFinite(n) ? `${n > 0 ? "+" : ""}${n.toFixed(1)}` : "—");
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { timeZone: "Europe/Athens", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "live feed");

function Card({ k, title, sub, s, d, away, home }) {
  return (
    <article className={`sc-card sc-${k}`} data-testid={`sc-card-${k}`}>
      <div className="sc-card-title">{title}</div>
      <div className="sc-match"><div><small>{away}</small><strong data-testid={`sc-${k}-away`}>{f1(s.away)}</strong></div><span>—</span><div><small>{home}</small><strong data-testid={`sc-${k}-home`}>{f1(s.home)}</strong></div></div>
      <div className="sc-total">TOTAL <strong data-testid={`sc-${k}-total`}>{f1(s.total)}</strong></div>
      {d && <div className="sc-delta">vs market {away} {sg(d.away)} · {home} {sg(d.home)} · T {sg(d.total)}</div>}
      <p>{sub}</p>
    </article>
  );
}

function Bet365Editor({ game, onSaved }) {
  const [f, setF] = useState({ spread_home: "", total: "" });
  const [busy, setBusy] = useState(false);
  const pick = (e) => { const files = [...(e.target.files || [])]; e.target.value = ""; if (files.length) scan(files); };
  const scan = async (files) => {
    const fd = new FormData(); files.forEach((x) => fd.append("files", x));
    setBusy(true);
    try { const r = await api.post(`/team-value/${game.id}/scan`, fd); setF({ spread_home: r.data.lines?.spread_home ?? "", total: r.data.lines?.total ?? "" }); toast.success("Check the lines read, then save"); } catch (err) { toast.error(errorText(err)); }
    setBusy(false);
  };
  const save = async () => {
    try { await api.put(`/season-model/bet365-lines/${game.id}`, { spread_home: Number(f.spread_home), total: Number(f.total) }); toast.success("bet365 lines verified"); onSaved(); } catch (err) { toast.error(errorText(err)); }
  };
  const clear = async () => { try { await api.delete(`/season-model/bet365-lines/${game.id}`); setF({ spread_home: "", total: "" }); toast.success("bet365 lines removed"); onSaved(); } catch (err) { toast.error(errorText(err)); } };
  return (
    <ShotDrop onFiles={scan} busy={busy} testId="sc-bet365-drop">
    <div className="sc-editor" data-testid="sc-bet365-editor">
      <label>{game.home_code} handicap<input type="number" step="0.5" value={f.spread_home} onChange={(e) => setF({ ...f, spread_home: e.target.value })} placeholder="-3.5" data-testid="sc-bet365-spread" /></label>
      <label>Game total<input type="number" step="0.5" value={f.total} onChange={(e) => setF({ ...f, total: e.target.value })} placeholder="218.5" data-testid="sc-bet365-total" /></label>
      <label className="sc-btn"><Camera size={13} />{busy ? "Reading…" : "Scan"}<input type="file" accept="image/png,image/jpeg,image/webp" hidden multiple onChange={pick} disabled={busy} data-testid="sc-bet365-file" /></label>
      <button type="button" className="sc-btn primary" onClick={save} disabled={f.spread_home === "" || f.total === ""} data-testid="sc-bet365-save"><Save size={13} />Verify bet365</button>
      <button type="button" className="sc-btn" onClick={clear} data-testid="sc-bet365-clear"><RotateCcw size={13} />Use DraftKings</button>
    </div>
    </ShotDrop>
  );
}

export function ScoreComparison({ game }) {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/season-model/score-comparison", { params: { game_id: game.id } }).then((r) => setD(r.data)).catch((e) => setD({ status: "unavailable", reason: errorText(e) })), [game.id]);
  useEffect(() => { setD(null); setOpen(false); load(); }, [load]);
  const a = game.away_code, h = game.home_code;
  return (
    <section className="sc-root" data-testid="tp-score-comparison">
      <div className="sc-head"><div><span className="sc-eyebrow">TEAM PROJECTIONS · NEW MODEL</span><h3>SCORE COMPARISON</h3></div>{d?.model_version && <span className="sc-ver">{d.model_version}</span>}</div>
      {!d ? <p className="sc-note">Calculating…</p> : d.status !== "ok" ? <p className="sc-note" data-testid="sc-unavailable">Unavailable · {d.reason}</p> : (
        <>
          <div className="sc-grid">
            <Card k="market" title={`SCORE ${d.market_source === "bet365" ? "bet365" : "DraftKings"}`} sub={`Market-implied (${h} ${sg(d.market.home_spread)}, total ${f1(d.market.total)})`} s={d.market} away={a} home={h} />
            <Card k="raw" title="NBACUBE RAW" sub="Sum of player points, 240/240" s={d.raw} d={d.delta_raw} away={a} home={h} />
            <Card k="adjusted" title="NBACUBE ADJUSTED" sub="Within ±10 per team, ±12 total" s={d.adjusted} d={d.delta_adjusted} away={a} home={h} />
          </div>
          <div className="sc-bottom" data-testid="sc-source">
            <span>Source: {d.market_source}{d.market_source === "bet365" ? ` · verified by ${d.market_verified_by}` : d.market_status === "screenshot" ? " · screenshot" : " · ESPN (no bet365 line yet)"} · {when(d.market_captured_at)}</span>
            <span>Implied score is not a bookmaker score prediction; Adjusted is market-constrained.</span>
          </div>
          <button type="button" className="sc-btn" onClick={() => setOpen(!open)} data-testid="sc-players-toggle">{open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}Player redistribution</button>
          {open && (
            <div className="sc-tablewrap"><table className="sc-table" data-testid="sc-players">
              <thead><tr><th>PLAYER</th><th>ROLE</th><th>MIN</th><th>RAW PTS</th><th>ADJ PTS</th><th>CHANGE</th></tr></thead>
              <tbody>{d.players.map((p) => (
                <tr key={p.player_id}><td>{p.name}<small>{p.side === "away" ? a : h}</small></td><td>{p.starter ? "S" : "B"}</td><td>{f1(p.minutes)}</td><td>{f1(p.raw_pts)}</td><td>{f1(p.adjusted_pts)}</td><td>{sg(p.adjusted_pts - p.raw_pts)}</td></tr>
              ))}</tbody>
            </table></div>
          )}
        </>
      )}
      {game.status === "pre" && <Bet365Editor game={game} onSaved={load} />}
    </section>
  );
}
